import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "Components/ReactstrapSlim";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouterProps } from "react-router-dom";
import { isLegacyException, isResponseWrapper } from "Utils/typeGuards";

type PageLeaveCallback = (ok: boolean) => void;
type PageLeaveConfirmationOptions = LiquidSpace.View.PageLeaveConfirmationOptionsCore;

export const usePageLeaveConfirmation = (subscriptionId: string, options: PageLeaveConfirmationOptions, deps: React.DependencyList) => {
   const layoutContext = useContext(LayoutContext);
   useEffect(() => {
      if (layoutContext.onPageLeave)
         layoutContext.onPageLeave(subscriptionId, options);
      return function cancel() {
         if (layoutContext.offPageLeave)
            layoutContext.offPageLeave(subscriptionId)
      }
   }, deps)
}

const PAGE_LEAVE_MESSAGE = "You have unsaved changes. Do you want to save before navigating away from this page?";

const getPageLeaveMessage = (message?: string): string => {
   return message || PAGE_LEAVE_MESSAGE;
}

export interface ErrorState {
   message: string,
   title?: string,
   errorCode?: string,
   debugTrace?: string,
   onClose?: () => void,
}

export interface ILayoutContext {
   showMessage(message: string, timeout?: number): void;
   errorState: ErrorState | undefined;
   showError(error: unknown, title?: string, onClose?: () => void): void;
   isLoading: boolean,
   setIsLoading: (isLoading: boolean) => void,
   hasPageLeaveCallbacks: () => boolean,
   getUserConfirmation?: BrowserRouterProps["getUserConfirmation"],
   onPageLeave?: (id: string, options: PageLeaveConfirmationOptions) => void,
   offPageLeave?: (id: string) => void,

}
export const LayoutContext = createContext<ILayoutContext>({
   showMessage(message, timeout) { return void 0; },
   errorState: undefined,
   showError(error, title, onClose) { return void 0; },
   isLoading: false,
   setIsLoading(isLoading) { return void 0; },
   hasPageLeaveCallbacks() { return false },
});

export const LayoutContextProvider: React.FC = function ({ children }) {
   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [errorState, setErrorState] = useState<ErrorState>();
   const onPageLeaveStack = useRef<{ [key: string]: PageLeaveConfirmationOptions }>({});

   const [pageLeaveCallback, setPageLeaveCallback] = useState<PageLeaveCallback | undefined>();
   const [pageLeaveModalOptions, setPageLeaveModalOptions] = useState<PageLeaveConfirmationOptions | undefined>();

   const hasPageLeaveCallbacks = () => {
      return Object.keys(onPageLeaveStack.current).length > 0
   }

   const registerOnPageLeave = (id: string, options: PageLeaveConfirmationOptions) => {
      onPageLeaveStack.current[id] = options;
   }

   const removeOnPageLeave = (id: string) => {
      if (id in onPageLeaveStack.current)
         delete onPageLeaveStack.current[id];
   }

   const showOnPageLeaveModal = (options: PageLeaveConfirmationOptions, callback: PageLeaveCallback) => {
      setPageLeaveCallback(() => callback);
      setPageLeaveModalOptions(options);
   }

   const closeOnPageLeaveModal = () => {
      closeOnPageLeaveModalInner(true);
   }

   const closeOnPageLeaveModalInner = (proceedToDestination) => {
      if (pageLeaveCallback) {
         pageLeaveCallback(proceedToDestination);
         setPageLeaveCallback(undefined);
         setPageLeaveModalOptions(undefined);
      }
   }


   const confirmPageLeaveModal = () => {
      if (pageLeaveModalOptions) {
         pageLeaveModalOptions.confirmAction(closeOnPageLeaveModalInner);
         setPageLeaveModalOptions(undefined);
      }
   }

   const getUserConfirmation: BrowserRouterProps["getUserConfirmation"] = (message, callback) => {
      if (hasPageLeaveCallbacks()) {
         const callbackWrapper = (ok: boolean) => {
            callback(ok);
            onPageLeaveStack.current = {};
         }
         for (const options of Object.values(onPageLeaveStack.current)) {
            if (options.shouldDisplay()) {
               showOnPageLeaveModal(options, callbackWrapper);
               return;
            }
         }
         callbackWrapper(true);
      } else {
         callback(true);
      }
   }

   useEffect(() => {
      const onBeforeUnload = (event: BeforeUnloadEvent): string | void => {
         for (const options of Object.values(onPageLeaveStack.current)) {
            if (options.shouldDisplay()) {
               return event.returnValue = getPageLeaveMessage(options.message);
            }
         }
      }

      window.addEventListener("beforeunload", onBeforeUnload);

      return function cancel() {
         window.removeEventListener("beforeunload", onBeforeUnload);
      }
   }, [])

   const layoutContextProvider = useMemo((): ILayoutContext => ({
      showMessage: (message, timeout) => {
         console.log("showMessage", message);
      },
      errorState,
      showError: (error, title, onClose) => {
         const currentError: ErrorState = {
            message: "Unexpected Server Error",
            title: title,
            onClose: onClose
         };

         let errorObj = error;
         if (typeof error === "string") {
            //First check if it's a json from API exception
            try {
               errorObj = JSON.parse(error);
            } catch {
               //If not - consider it an exception message string
               if (error)
                  currentError.message = error;
            }
         }

         if (isResponseWrapper(errorObj)) {
            if (errorObj.errorMessage)
               currentError.message = errorObj.errorMessage;
            currentError.errorCode = errorObj.errorCode;
            currentError.debugTrace = errorObj.detailedDebugException;
         }
         else if (isLegacyException(errorObj)) {
            if (errorObj.errorMessage)
               currentError.message = errorObj.errorMessage;
            currentError.errorCode = errorObj.errorCode ?? undefined;
            currentError.title = errorObj.title ?? undefined;
         } else if (errorObj instanceof Error) {
            currentError.message = errorObj.message;
         }

         setErrorState(currentError);
      },
      isLoading: isLoading,
      setIsLoading: setIsLoading,
      hasPageLeaveCallbacks: hasPageLeaveCallbacks,
      getUserConfirmation: getUserConfirmation,
      onPageLeave: registerOnPageLeave,
      offPageLeave: removeOnPageLeave
   }), [isLoading, errorState]);

   const closeErrorDialog = useCallback(() => {
      if(errorState && errorState.onClose) {
         errorState.onClose();
      }
      setErrorState(undefined);
   }, [errorState]);

   return (<LayoutContext.Provider value={layoutContextProvider}>
      {children}
      {
         errorState
            ? <Modal centered isOpen trapFocus size={errorState.debugTrace ? "xl" : undefined}>
               <ModalHeader>{errorState.title ?? "Warning"}</ModalHeader>
               <ModalBody className="ls-modal-body">
                  <div className="text-danger">{errorState.message}</div>
                  <div className="mt-2">If you continue to experience this problem, please contact <a href="mailto:support@liquidspace.com">support@liquidspace.com</a>{errorState.errorCode ? ` and refer to error ${errorState.errorCode}.` : "."}</div>
                  {
                     errorState.debugTrace
                        ? <pre className="sb" style={{ whiteSpace: "pre-wrap", font: "12px monospace", overflow: "auto", maxHeight: "240px" }}>
                           {errorState.debugTrace}
                        </pre>
                        : null
                  }
               </ModalBody>
               <ModalFooter>
                  <Button type="button" color="primary" onClick={closeErrorDialog}>OK</Button>
               </ModalFooter>
            </Modal>
            : null
      }
      {
         pageLeaveModalOptions
            ? <Modal centered isOpen trapFocus toggle={closeOnPageLeaveModal} style={{ maxWidth: "min(100%, 580px)" }}>
               <ModalHeader toggle={closeOnPageLeaveModal}>{pageLeaveModalOptions.title ?? "You have unsaved changes."}</ModalHeader>
               <ModalBody>
                  {pageLeaveModalOptions.message ? pageLeaveModalOptions.message : <div>
                     Do you want to save before navigating away from this page?
                  </div>}
               </ModalBody>
               <ModalFooter>
                  <Button type="button" color="secondary" onClick={() => closeOnPageLeaveModal()}>{pageLeaveModalOptions.cancelButtonText || "Don't save"}</Button>
                  <Button type="button" color="primary" onClick={() => confirmPageLeaveModal()}>{pageLeaveModalOptions.confirmButtonText || "Save"}</Button>
               </ModalFooter>
            </Modal>
            : null
      }
   </LayoutContext.Provider>);
}